import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-limo-4.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/winery-tour-transportation-atlanta";
const PAGE_TITLE = "Atlanta Winery Tour Transportation | Dahlonega Wine Tours";
const PAGE_DESC =
  "Chauffeured Mercedes-Benz Sprinter wine tours from Atlanta to the Dahlonega and North Georgia wine trail, plus Atlanta brewery loops. Call (678) 431-7222.";

const faqs = [
  {
    q: "How long is a Dahlonega wine tour day from Atlanta?",
    a: "Plan on eight to ten hours door to door. The drive north on GA-400 is roughly 75 to 90 minutes each way, and three or four tastings with a lunch stop fills the middle comfortably. Groups that try to fit five wineries into a day spend the afternoon watching the clock instead of the vineyard.",
  },
  {
    q: "How many wineries should we book in one day?",
    a: "Three is the sweet spot, four if the properties are close together. Wolf Mountain, Montaluce, Cavender Creek, and Frogtown sit within a short drive of one another, so a four-stop day works there. Spreading across Dahlonega and Helen in the same day does not.",
  },
  {
    q: "Do you make the tasting reservations for us?",
    a: "We do not book the wineries, most require the party name on the reservation and have their own group policies. What we do is build the driving schedule around the reservation times you hold, including realistic buffers so you are never arriving late to a seated tasting.",
  },
  {
    q: "Can we bring food and drinks in the vehicle?",
    a: "Yes. The wet bar has ice wells and chilled storage, and most groups bring a cooler for the ride up and case space for what they buy on the way back. There is separate rear cargo, so purchases do not ride in the cabin with you.",
  },
  {
    q: "Is the chauffeur with us all day or do they leave between stops?",
    a: "The chauffeur stays with your group for the full booked block. That is the point of an hourly booking, the vehicle is at the winery when the tasting runs long, and there is nothing to re-summon between stops.",
  },
  {
    q: "Do you run Atlanta brewery tours too?",
    a: "Regularly. West Midtown, Chamblee, and Decatur have enough density for a four-stop loop inside a five-hour block, with the chauffeur waiting on-site between each. It is the same booking structure as a wine day, just shorter.",
  },
  {
    q: "What size group fits a wine tour Sprinter?",
    a: "Up to fourteen passengers in the Mercedes-Benz Sprinter Limousine, or up to thirteen in an Executive Sprinter with facing captain's chairs if your group prefers a conversational layout over lounge seating. Larger parties travel as two vehicles travelling together.",
  },
];

export const Route = createFileRoute("/winery-tour-transportation-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "atlanta winery tour transportation, dahlonega wine tour from atlanta, north georgia wine tour limo, atlanta brewery tour transportation, sprinter wine tour atlanta, chauffeured wine tour georgia",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Winery & Brewery Tour Transportation",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Winery and Brewery Tour Transportation",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Dahlonega" },
              { type: "City", name: "Helen" },
              { type: "City", name: "Braselton" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Wine Country • North Georgia"
      h1="Winery & Brewery Tour Transportation from Atlanta"
      intro="You've picked the wineries. I'll plan the drive from Atlanta to Dahlonega, North Georgia, Braselton or your favorite brewery district, with your chauffeur staying at every stop."
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter Limousine lounge interior set for a North Georgia winery tour from Atlanta"
      midImage={midImage}
      midAlt="Executive Sprinter cabin with facing seating for an Atlanta wine tour group"
      sectionOneTitle="Enjoy the Tastings. We'll Handle the Drive."
      sectionOneBody={[
        "A North Georgia wine day means roughly three hours of driving around four hours of tasting. My chauffeur handles GA-400 while you enjoy the day.",
        "Your hourly booking keeps the vehicle at each property. Cases travel in the rear cargo bay, and the Sprinter's stand-up cabin offers lounge seating or facing captain's chairs.",
        "Atlanta Luxury Sprinters is the luxury Sprinter fleet of Empress Elite Limousine, my family's company.",
      ]}
      bulletsTitle="What a Tour Day Includes"
      bulletsIntro="One vehicle, one chauffeur, and a schedule built around the reservations you already hold."
      bullets={[
        "Dedicated chauffeur for the full block, the vehicle waits at every stop",
        "Driving schedule built around your tasting reservation times",
        "Separate rear cargo bay for cases and coolers",
        "Wet bar with ice wells and chilled storage for the ride up",
        "Stand-up cabin height with lounge or facing captain's-chair seating",
        "Multiple pickups across metro Atlanta before heading north",
        "Fixed hourly rate confirmed in writing before the day",
      ]}
      coverageTitle="Wine Country and Brewery Districts We Cover"
      coverageIntro="Day trips north from any metro Atlanta pickup, plus in-town brewery loops on a shorter block."
      coverage={[
        "Dahlonega wine trail",
        "Wolf Mountain Vineyards",
        "Montaluce Winery",
        "Frogtown Cellars",
        "Cavender Creek Vineyards",
        "Chateau Elan, Braselton",
        "Helen & Sautee Nacoochee",
        "Blue Ridge",
        "West Midtown breweries",
        "Chamblee & Doraville breweries",
        "Decatur brewery district",
        "Atlanta, Buckhead & Alpharetta pickups",
      ]}
      routeTable={{
        title: "Tour Routes and Realistic Timing",
        intro:
          "Drive times from our own trip history, so your day's schedule survives contact with GA-400 on a Saturday.",
        rows: [
          {
            route: "Atlanta → Dahlonega wine trail",
            distance: "~65 mi",
            time: "75–95 min",
            notes: "GA-400 N. Saturday northbound is slowest 9–11am; earlier departures help.",
          },
          {
            route: "Dahlonega → Wolf Mountain → Montaluce",
            distance: "4–9 mi between",
            time: "10–20 min each",
            notes: "Tight cluster, the reason most four-stop days are built here.",
          },
          {
            route: "Atlanta → Chateau Elan, Braselton",
            distance: "~45 mi",
            time: "50–75 min",
            notes: "I-85 N. Winery, spa, and golf on one property, good half-day option.",
          },
          {
            route: "Atlanta → Helen / Sautee Nacoochee",
            distance: "~85 mi",
            time: "1h 45m–2h",
            notes: "Best as its own day rather than paired with Dahlonega.",
          },
          {
            route: "Atlanta → Blue Ridge",
            distance: "~95 mi",
            time: "1h 45m–2h 15m",
            notes: "GA-515. Mountain grades; allow extra on the return after dark.",
          },
          {
            route: "West Midtown brewery loop",
            distance: "~12 mi total",
            time: "4–5 hr block",
            notes: "3–4 stops, chauffeur waits on-site between each.",
          },
          {
            route: "Chamblee + Decatur brewery loop",
            distance: "~25 mi total",
            time: "5 hr block",
            notes: "Wider spread; four stops is the practical maximum.",
          },
          {
            route: "Return leg, wine country → Atlanta",
            distance: "45–95 mi",
            time: "60 min–2h 15m",
            notes: "Sunday afternoon southbound 400 adds 30 min. Built into every quote.",
          },
        ],
        footnote:
          "Hourly bookings include all waiting time at each property, there is no per-stop charge and no re-booking between stops.",
      }}
      detailSections={[
        {
          title: "How to Build a Day That Actually Works",
          body: [
            "The most common mistake on a Dahlonega day is over-scheduling. Tastings run longer than the website says, lunch takes an hour, and the drive between two properties that look adjacent on a map can be twenty minutes of two-lane road.",
          ],
          list: [
            "Depart Atlanta by 9:30am for a three-winery day with lunch; 8:30am if you want four.",
            "Hold your tasting reservations first, we build the driving schedule around them.",
            "Book lunch at a winery with a kitchen rather than adding a separate town stop.",
            "Leave the last property by 5:00pm to stay ahead of the southbound return.",
            "Tell us your case count expectations so we stage the cargo bay accordingly.",
          ],
        },
        {
          title: "Bachelorette Parties, Birthdays, and Corporate Outings",
          body: [
            "Wine days are the most-requested group occasion we run outside of weddings. Bachelorette parties use the full Sprinter Limousine with the wet bar stocked for the ride up; corporate teams tend to prefer the Executive Sprinter, where facing captain's chairs make conversation possible at highway speed.",
            "Either way the booking is the same: one hourly block, one chauffeur, and an itinerary confirmed in writing the week before.",
          ],
        },
        {
          title: "In-Town Brewery Loops",
          body: [
            "If a full day north is more than your group wants, an Atlanta brewery loop delivers the same structure in five hours. West Midtown alone supports three or four stops within a few miles, and Chamblee and Decatur add range if you want variety.",
          ],
        },
      ]}
      gallery={{
        title: "Tour Day Fleet",
        intro: "Sprinter Limousines for lounge-style groups, Executive Sprinters for facing conversation seating.",
        images: [
          { src: galleryA, alt: "Mercedes-Benz Sprinter Limousine cabin for a North Georgia wine tour", caption: "Sprinter Limousine, up to 14" },
          { src: galleryB, alt: "Wet bar with ice wells inside a Sprinter Limousine for Atlanta wine tours", caption: "Wet bar with chilled storage" },
          { src: galleryC, alt: "Executive Sprinter interior with facing captain's chairs for a corporate wine outing", caption: "Executive Sprinter, up to 13" },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta Wine & Brewery Tour FAQs"
      relatedTitle="Related Occasion Services"
      related={[
        { to: "/wedding-transportation-atlanta", title: "Wedding Transportation Atlanta", desc: "Bridal party, couple's transfer, and guest loops." },
        { to: "/concert-sports-transportation-atlanta", title: "Concert & Sports Transportation", desc: "Stadium, arena, and amphitheater event nights." },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The full Sprinter Limousine fleet and configurations." },
      ]}
      ctaTitle="Text Me Your Wine Tour Plans"
      ctaBody="Text me your date, pickup, group size and tasting reservations. I'll plan the driving schedule and send your hourly quote in writing."
    />
  );
}
